import { create } from 'zustand';

const STORAGE_KEY = 'omni.storefront.cart';
const DROPSHIP_REF_KEY = 'omni.storefront.dropshipRef';

function readDropshipRef(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(DROPSHIP_REF_KEY) || null;
}
function writeDropshipRef(ref: string | null) {
  if (typeof window === 'undefined') return;
  try {
    if (ref) window.localStorage.setItem(DROPSHIP_REF_KEY, ref);
    else window.localStorage.removeItem(DROPSHIP_REF_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Client-side cart (the backend has no server cart — order is submitted whole at
 * checkout). Lines are keyed by productId + variation so the same product in two
 * variations are distinct rows. We snapshot name/price/image so the cart + checkout
 * render without refetching, mirroring how a real cart persists chosen options.
 */
export type CartLine = {
  key: string;
  productId: string;
  name: string;
  price: number; // effective unit price (cash)
  was: number; // strikethrough unit price (for "You save")
  // Flat GC/coin sticker prices (no discount), synced from the catalog. null =
  // not sold in that currency → GC/coin checkout disables the line.
  priceGc?: number | null;
  priceCoin?: number | null;
  image: string | null;
  variation: string;
  // Set when the line is a specific product variation; the API receives this to
  // price + reserve the right variant. Empty/undefined = single-SKU product.
  variantId?: string;
  qty: number;
  checked: boolean;
  pv: number;
  // Buyer cashback per unit = Realtime Unilevel Level 0, snapshotted from the
  // catalog (re-synced on login). Undefined = unknown (logged-out) → treated 0.
  cashback?: number;
  sellerId: string; // '' = Omni Official (house); else merchantUserId / creatorUserId
  sellerName: string;
  // LMS: 'creator' marks a course line owned by a Content Creator — checkout
  // routes the seller group to POST /creator-orders (no shipping quote; the
  // creator is credited at completion). Undefined = house or merchant.
  sellerKind?: 'creator';
  // Available units (variant-aware) synced from the live catalog. Caps qty so a
  // buyer can't check out more than is in stock (the backend also rejects).
  // undefined = unknown (logged-out / not in the synced page) → not capped here.
  stock?: number;
  // LMS: this line is a COURSE (always virtual too). Lets checkout send a
  // finished buyer to their classroom instead of to order management.
  isCourse?: boolean;
  // Virtual product / service — nothing is delivered for this line. A cart made
  // ENTIRELY of these needs no address and no courier at checkout; a mixed cart
  // still ships its physical lines as usual. Undefined = unknown (logged-out)
  // → treated as physical, which fails safe toward asking for the address.
  isVirtual?: boolean;
  // A creator subscription plan the buyer asked to renew automatically. Sent
  // with the line; the server honours it only on a cash WALLET checkout.
  autoRenew?: boolean;
  // A creator subscription PLAN line, by its period and creator. Lets checkout
  // say what a card subscription will bill before the buyer leaves for the
  // card page, and stay quiet when a card subscription already renews it.
  planPeriod?: 'monthly' | 'semi_annual' | 'annual';
  planCreatorId?: string;
};

export type AddLine = Omit<CartLine, 'key' | 'qty' | 'checked'> & { qty?: number };

type State = {
  lines: Record<string, CartLine>;
  /**
   * Dropshipping Income: the `ref` from a `/product/{id}?ref=...` link the
   * buyer arrived through (plaintext code or encrypted `e1.` token). Persisted so
   * it survives browse → cart → checkout, where it tags the order to the sharer.
   */
  dropshipRef: string | null;
  setDropshipRef: (ref: string | null) => void;
  add: (line: AddLine) => void;
  /**
   * Add a line and leave it as the ONLY checked line, without deleting anything.
   * The `/buy` campaign funnel sells exactly one product: whatever the visitor
   * already had must neither be billed alongside it nor destroyed.
   *
   * NOT `clear()` — that also resets `dropshipRef`, which the funnel sets just
   * before adding, so clearing would silently drop the sharer's attribution.
   */
  addOnly: (line: AddLine) => void;
  setQty: (key: string, qty: number) => void;
  setChecked: (key: string, checked: boolean) => void;
  setCheckedMany: (keys: string[], checked: boolean) => void;
  remove: (key: string) => void;
  removeMany: (keys: string[]) => void;
  clear: () => void;
  /**
   * On logout, drop any member-only discount snapshot: revert each line's unit
   * price to its base (the `was` value) and clear the strikethrough so a logged-
   * out buyer never sees (or is charged a display price from) a member discount.
   */
  revertToBasePrice: () => void;
  /**
   * On login, refresh each line's price to the buyer's effective (discounted)
   * price. Caller passes the per-buyer priced catalog (from `getStorefront()`);
   * we match by productId (and by variantId for a variation line). A line whose
   * product — or whose specific variant — is no longer in the catalog keeps its
   * snapshot; we never blank a price out from under a live checkout, and we
   * never fall back to the PARENT's price for a variant line (the parent and the
   * variant carry independent segment prices).
   *
   * `opts.dropshipActive` mirrors checkout's `isDropship`: a stored dropship ref
   * only forces SRP display when the tenant actually has Dropshipping Income on.
   */
  syncCartPrices: (
    priced: {
      id: string;
      priceEffective: string;
      priceBase: string;
      priceGc?: string | null;
      priceCoin?: string | null;
      cashback?: string;
      /** null = not stock-tracked (virtual); the qty cap below already skips it. */
      stockQty?: number | null;
      isVirtual?: boolean;
      variants?: {
        id: string;
        stockQty: number;
        /** The variant's own effective price. Optional so partial fixtures still fit. */
        price?: string;
        /** Compare-at price — the server sets it ONLY when the variant is discounted. */
        srp?: string | null;
        priceGc?: string | null;
        priceCoin?: string | null;
      }[];
    }[],
    opts?: { dropshipActive?: boolean },
  ) => void;
};

// Lines are distinct per (product, variant). Falls back to the variation label
// for legacy lines that predate variant ids.
const lineKey = (productId: string, variantId?: string, variation = '') =>
  `${productId}::${variantId ?? variation}`;

/**
 * Is this a purely digital purchase — every line a virtual product/service?
 *
 * Decides which checkout the buyer sees, so it FAILS SAFE: `isVirtual` is
 * `boolean | undefined`, and an unknown (a logged-out snapshot, or a line
 * predating the field) reads as PHYSICAL and keeps the marketplace layout. That
 * mirrors `deliveryRequired` in checkout.tsx, which fails safe toward asking for
 * an address — the two must agree, and `some(!isVirtual)` is exactly the
 * negation of `every(isVirtual)` over a non-empty list.
 *
 * An EMPTY cart is not "all virtual": there is nothing to infer a layout from.
 *
 * Exported as a pure function because `CheckoutPage` itself can't be rendered in
 * this app's test setup (no jsdom), and this predicate is the single branch
 * point deciding what every tenant sees.
 */
export function isAllVirtual(items: Pick<CartLine, 'isVirtual'>[]): boolean {
  return items.length > 0 && items.every((l) => l.isVirtual === true);
}

function readStored(): Record<string, CartLine> {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(STORAGE_KEY) ?? '{}');
  } catch {
    return {};
  }
}
function writeStored(lines: Record<string, CartLine>) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
  } catch {
    /* ignore */
  }
}

export const useCartStore = create<State>((set) => ({
  lines: readStored(),
  dropshipRef: readDropshipRef(),
  setDropshipRef: (ref) => {
    writeDropshipRef(ref);
    return set({ dropshipRef: ref });
  },
  add: (line) =>
    set((s) => {
      const key = lineKey(line.productId, line.variantId, line.variation);
      const existing = s.lines[key];
      const lines = {
        ...s.lines,
        [key]: existing
          ? { ...existing, qty: line.isVirtual ? 1 : existing.qty + (line.qty ?? 1) }
          : { ...line, key, qty: line.isVirtual ? 1 : (line.qty ?? 1), checked: true },
      };
      writeStored(lines);
      return { lines };
    }),
  addOnly: (line) =>
    set((s) => {
      const key = lineKey(line.productId, line.variantId, line.variation);
      const existing = s.lines[key];
      const lines: Record<string, CartLine> = {};
      // Uncheck, never delete. The `/buy` funnel sells exactly ONE thing, so an
      // existing cart must not be billed alongside it — but it isn't ours to
      // throw away either. Checkout reads only `checked` lines, so unchecking is
      // sufficient. Deliberately NOT `clear()`: that also resets `dropshipRef`,
      // which buy.tsx sets immediately BEFORE adding, so clearing after would
      // erase the sharer's attribution and clearing before would erase an
      // earlier link's.
      for (const [k, l] of Object.entries(s.lines))
        lines[k] = l.checked ? { ...l, checked: false } : l;
      // Add AFTER the uncheck sweep: `add` leaves an EXISTING line's `checked`
      // untouched, so sweeping second would leave the funnel's own line off.
      // `autoRenew` follows the latest pick: re-adding a plan with the box
      // unticked must not keep an earlier consent alive.
      lines[key] = existing
        ? {
            ...existing,
            qty: line.isVirtual ? 1 : existing.qty,
            autoRenew: line.autoRenew,
            checked: true,
          }
        : { ...line, key, qty: line.isVirtual ? 1 : (line.qty ?? 1), checked: true };
      writeStored(lines);
      return { lines };
    }),
  setQty: (key, qty) =>
    set((s) => {
      const cur = s.lines[key];
      if (!cur) return s;
      const lines = { ...s.lines };
      if (qty <= 0) {
        delete lines[key];
      } else {
        // A virtual line is sold one per order — there is nothing to count. Note
        // this sits AFTER the `qty <= 0` branch so `setQty(key, 0)` still removes.
        // Otherwise: cap at the known available stock (variant-aware) so the
        // cart/checkout steppers can't exceed it. Unknown stock (undefined) is
        // left uncapped — but a virtual product's stock is ALWAYS unknown (it
        // isn't inventory-tracked), which is why the virtual case leads.
        const capped = cur.isVirtual
          ? 1
          : cur.stock != null
            ? Math.min(qty, Math.max(1, cur.stock))
            : qty;
        lines[key] = { ...cur, qty: capped };
      }
      writeStored(lines);
      return { lines };
    }),
  setChecked: (key, checked) =>
    set((s) => {
      if (!s.lines[key]) return s;
      const lines = { ...s.lines, [key]: { ...s.lines[key], checked } };
      writeStored(lines);
      return { lines };
    }),
  setCheckedMany: (keys, checked) =>
    set((s) => {
      const lines = { ...s.lines };
      keys.forEach((k) => {
        if (lines[k]) lines[k] = { ...lines[k], checked };
      });
      writeStored(lines);
      return { lines };
    }),
  remove: (key) =>
    set((s) => {
      const lines = { ...s.lines };
      delete lines[key];
      writeStored(lines);
      return { lines };
    }),
  removeMany: (keys) =>
    set((s) => {
      const lines = { ...s.lines };
      keys.forEach((k) => delete lines[k]);
      writeStored(lines);
      return { lines };
    }),
  clear: () => {
    writeStored({});
    writeDropshipRef(null);
    return set({ lines: {}, dropshipRef: null });
  },
  revertToBasePrice: () =>
    set((s) => {
      const lines = Object.fromEntries(
        Object.entries(s.lines).map(([k, l]) => [
          k,
          { ...l, price: l.was > 0 ? l.was : l.price, was: 0 },
        ]),
      );
      writeStored(lines);
      return { lines };
    }),
  syncCartPrices: (priced, opts) =>
    set((s) => {
      const byId = new Map(priced.map((p) => [p.id, p]));
      const lines = Object.fromEntries(
        Object.entries(s.lines).map(([k, l]) => {
          const p = byId.get(l.productId);
          if (!p) return [k, l]; // no longer purchasable — keep the snapshot
          // Variant-aware resolution: a variation line reads its OWN price, stock
          // and GC/coin — never the parent's. The parent and each variant carry
          // independent membership / rank / non-activated segment prices, so
          // repricing a variant line from `p.priceEffective` silently replaces the
          // buyer's discounted variant price with the parent's list price.
          const variant = l.variantId ? p.variants?.find((v) => v.id === l.variantId) : undefined;
          // The variant is gone from the catalog (unpublished / deleted) or the
          // caller passed a catalog without variant prices: keep the snapshot
          // rather than falling back to the parent, same philosophy as the
          // missing-product guard above.
          if (l.variantId && variant?.price == null) return [k, l];
          // `srp` is non-null only when the variant is discounted, so an
          // undiscounted variant collapses base → effective and `was` stays 0.
          // (Do NOT use toDisplay()'s `srp ? Number(srp) : 0` form here: it would
          // give an undiscounted variant was=0, and revertToBasePrice() on logout
          // would then leave the member price in the cart.)
          const rawEffective =
            variant?.price != null ? Number(variant.price) : Number(p.priceEffective);
          const base =
            variant?.price != null
              ? variant.srp != null
                ? Number(variant.srp)
                : rawEffective
              : Number(p.priceBase);
          // Dropship carts charge SRP by design (the sharer's margin is measured
          // against base) — createOrderInTx exempts dropship from BOTH
          // non-activated pricing mechanisms. Showing the buyer-effective
          // discount here would display a price the checkout won't honor.
          // Gated on the tenant flag too (KEEP IN SYNC with checkout.tsx
          // `isDropship`): the ref persists in localStorage until an order is
          // placed, so a stale ref on a tenant without Dropshipping Income would
          // otherwise show SRP for a checkout that charges the discount.
          const effective = opts?.dropshipActive && s.dropshipRef && base > 0 ? base : rawEffective;
          const stock = l.variantId ? variant?.stockQty : p.stockQty;
          // The CATALOG's flag wins over the line's snapshot, so flipping a
          // product to virtual retro-normalises carts that already hold it.
          const virtual = p.isVirtual ?? l.isVirtual;
          // Pull qty back down if the live stock is now lower than what's in cart.
          // A virtual line is pinned to 1: `add`/`setQty` only fire when the user
          // acts, so a stale qty>1 line nobody touches would otherwise survive —
          // and this is the one pass that walks EVERY line.
          const qty = virtual ? 1 : stock != null && stock > 0 ? Math.min(l.qty, stock) : l.qty;
          // Same `was` semantics as toDisplay(), so a later logout revert is correct.
          const cashback = p.cashback != null ? Number(p.cashback) : l.cashback;
          // Flat GC/coin sticker prices (no discount), variant-aware.
          const gcSrc = l.variantId ? variant?.priceGc : p.priceGc;
          const coinSrc = l.variantId ? variant?.priceCoin : p.priceCoin;
          const priceGc = gcSrc == null ? null : Number(gcSrc);
          const priceCoin = coinSrc == null ? null : Number(coinSrc);
          return [
            k,
            {
              ...l,
              price: effective,
              was: base > effective ? base : 0,
              stock,
              qty,
              cashback,
              priceGc,
              priceCoin,
              isVirtual: virtual,
            },
          ];
        }),
      );
      writeStored(lines);
      return { lines };
    }),
}));

/** Total item count (sum of qty) — for the header cart badge. */
export function cartCount(lines: Record<string, CartLine>): number {
  return Object.values(lines).reduce((a, l) => a + l.qty, 0);
}
